import { useState, useRef } from 'react';
import { vehicles } from '../../../mocks/vehicles';

const FEES = {
  taxRate: 0.075,
  insuranceFee: 0,
  gpsRentalFee: 1.5,
  airportFee: 50,
  cleaningFee: 2,
  childSeatFee: 0,
};

const DISCOUNTS = {
  tier1: { minDays: 10, maxDays: 14, rate: 0.03 },
  tier2: { minDays: 15, maxDays: 19, rate: 0.05 },
  tier3: { minDays: 20, maxDays: Infinity, rate: 0.07 },
};

const getDiscount = (days: number) => {
  if (days >= DISCOUNTS.tier3.minDays) return DISCOUNTS.tier3.rate;
  if (days >= DISCOUNTS.tier2.minDays) return DISCOUNTS.tier2.rate;
  if (days >= DISCOUNTS.tier1.minDays) return DISCOUNTS.tier1.rate;
  return 0;
};

const formatDateBR = (dateStr: string) => {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-');
  return `${d}/${m}/${y}`;
};

type FlowStep = 'quote' | 'success';

interface QuoteData {
  days: number;
  pricePerDay: number;
  subtotal: number;
  discount: number;
  discountRate: number;
  insurance: number;
  gps: number;
  airportFee: number;
  cleaningFee: number;
  childSeats: number;
  childSeatFee: number;
  tax: number;
  total: number;
  vehicleName: string;
}

export default function QuoteGenerator() {
  const [pickupDate, setPickupDate] = useState('');
  const [returnDate, setReturnDate] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState('');
  const [includeGPS, setIncludeGPS] = useState(true);
  const [childSeats, setChildSeats] = useState(0);
  const [quote, setQuote] = useState<QuoteData | null>(null);

  // Fluxo
  const [flowStep, setFlowStep] = useState<FlowStep>('quote');

  // Modal de reserva
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [bookingSubmitting, setBookingSubmitting] = useState(false);

  // Dados do cliente
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientDocument, setClientDocument] = useState('');
  const [clientAddress, setClientAddress] = useState('');

  // Popup de sucesso
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  const contractRef = useRef<HTMLDivElement>(null);

  const calculateQuote = (e: React.FormEvent) => {
    e.preventDefault();
    if (!pickupDate || !returnDate || !selectedVehicle) return;

    const pickup = new Date(pickupDate);
    const returnD = new Date(returnDate);
    const days = Math.round((returnD.getTime() - pickup.getTime()) / (1000 * 60 * 60 * 24));

    if (days <= 0) {
      alert('A data de devolução deve ser posterior à data de retirada (mínimo 1 diária)');
      return;
    }

    const vehicle = vehicles.find(v => v.id === parseInt(selectedVehicle));
    if (!vehicle) return;

    const subtotal = days * vehicle.pricePerDay;
    const discountRate = getDiscount(days);
    const discount = subtotal * discountRate;
    const subtotalAfterDiscount = subtotal - discount;
    const insurance = days * FEES.insuranceFee;
    const gps = includeGPS ? days * FEES.gpsRentalFee : 0;
    const airportFee = FEES.airportFee;
    const cleaningFee = days * FEES.cleaningFee;
    const childSeatFee = childSeats * FEES.childSeatFee;
    const taxableAmount = subtotalAfterDiscount + insurance + gps + airportFee + cleaningFee + childSeatFee;
    const tax = taxableAmount * FEES.taxRate;
    const total = taxableAmount + tax;

    setQuote({
      days,
      pricePerDay: vehicle.pricePerDay,
      subtotal,
      discount,
      discountRate,
      insurance,
      gps,
      airportFee,
      cleaningFee,
      childSeats,
      childSeatFee,
      tax,
      total,
      vehicleName: vehicle.name,
    });
    setFlowStep('quote');
  };

  // Gerar PDF do orçamento (quote apenas)
  const generateQuotePDF = async () => {
    if (!quote) return;
    const { default: jsPDF } = await import('jspdf');
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = 210;
    const margin = 20;
    const contentWidth = pageWidth - margin * 2;
    let y = 20;

    const addTableRow = (label: string, value: string, highlight = false) => {
      if (highlight) {
        doc.setFillColor(245, 245, 245);
        doc.rect(margin, y - 4, contentWidth, 8, 'F');
      }
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(60, 60, 60);
      doc.text(label, margin + 2, y);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 30, 30);
      doc.text(value, pageWidth - margin - 2, y, { align: 'right' });
      y += 8;
    };

    // Header
    doc.setFillColor(30, 58, 138);
    doc.rect(0, 0, pageWidth, 35, 'F');
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('DRIVE WITH US', pageWidth / 2, 16, { align: 'center' });
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(200, 200, 255);
    doc.text('ORÇAMENTO DE LOCAÇÃO', pageWidth / 2, 26, { align: 'center' });
    const quoteNumber = `QT-${Date.now().toString(36).toUpperCase()}`;
    doc.setFontSize(8);
    doc.setTextColor(180, 180, 220);
    doc.text(`Orçamento Nº: ${quoteNumber}`, pageWidth / 2, 33, { align: 'center' });

    y = 45;
    const today = new Date();
    const dateStr = today.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text(`Orlando, FL - ${dateStr}`, pageWidth - margin, y, { align: 'right' });
    y += 10;

    // Aviso
    doc.setFillColor(255, 243, 205);
    doc.rect(margin, y - 4, contentWidth, 12, 'F');
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(146, 100, 10);
    doc.text('⚠  ESTE DOCUMENTO É APENAS UM ORÇAMENTO - NÃO CONSTITUI RESERVA', pageWidth / 2, y + 3, { align: 'center' });
    y += 18;

    // Veículo e Período
    doc.setFillColor(30, 58, 138);
    doc.rect(margin, y - 4, contentWidth, 8, 'F');
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('VEÍCULO E PERÍODO', margin + 4, y + 1);
    y += 12;

    addTableRow('Veículo', quote.vehicleName);
    addTableRow('Período', `${formatDateBR(pickupDate)} a ${formatDateBR(returnDate)}`, true);
    addTableRow('Total de Diárias', `${quote.days} ${quote.days === 1 ? 'diária' : 'diárias'}`);
    addTableRow('Valor da Diária', `$${quote.pricePerDay.toFixed(2)}`, true);
    if (quote.childSeats > 0) {
      addTableRow('Cadeirinha de Criança', `${quote.childSeats} unidade(s) - Grátis (VIP)`);
    }
    y += 4;

    // Resumo Financeiro
    doc.setFillColor(197, 48, 48);
    doc.rect(margin, y - 4, contentWidth, 8, 'F');
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('RESUMO FINANCEIRO', margin + 4, y + 1);
    y += 12;

    addTableRow(`Aluguel (${quote.days} diárias × $${quote.pricePerDay})`, `$${quote.subtotal.toFixed(2)}`);
    if (quote.discount > 0) {
      doc.setTextColor(22, 163, 74);
      addTableRow(`Desconto (${(quote.discountRate * 100).toFixed(0)}%)`, `-$${quote.discount.toFixed(2)}`, true);
      doc.setTextColor(30, 30, 30);
    }
    addTableRow('Seguro Completo', 'Incluído');
    if (quote.gps > 0) addTableRow(`Toll Coverage Fee`, `$${quote.gps.toFixed(2)}`, true);
    addTableRow('Taxa de Aeroporto', `$${quote.airportFee.toFixed(2)}`);
    addTableRow(`Surcharge (state fee)`, `$${quote.cleaningFee.toFixed(2)}`, true);
    addTableRow(`Impostos (${(FEES.taxRate * 100).toFixed(1)}%)`, `$${quote.tax.toFixed(2)}`);

    y += 2;
    doc.setFillColor(30, 58, 138);
    doc.rect(margin, y - 5, contentWidth, 12, 'F');
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('VALOR TOTAL ESTIMADO', margin + 4, y + 2);
    doc.text(`$${quote.total.toFixed(2)}`, pageWidth - margin - 4, y + 2, { align: 'right' });
    y += 20;

    // Validade
    doc.setFontSize(9);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(120, 120, 120);
    doc.text('Este orçamento é válido por 7 dias a partir da data de emissão e a depender da disponibilidade do automóvel.', pageWidth / 2, y, { align: 'center' });
    y += 5;
    doc.text('Para confirmar sua reserva, acesse nosso site ou entre em contato pelo WhatsApp +1 321 594 9330.', pageWidth / 2, y, { align: 'center' });

    // Footer
    const footerY = 285;
    doc.setDrawColor(30, 58, 138);
    doc.setLineWidth(0.5);
    doc.line(margin, footerY - 5, pageWidth - margin, footerY - 5);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(130, 130, 130);
    doc.text('DriveWith US • Orlando, FL • +1 (321) 594-9330 • dwusrental@gmail.com • @drivewitusoficial', pageWidth / 2, footerY, { align: 'center' });
    doc.text(`Documento gerado em ${today.toLocaleDateString('pt-BR')} às ${today.toLocaleTimeString('pt-BR')}`, pageWidth / 2, footerY + 4, { align: 'center' });

    doc.save(`Orcamento_DWUS_${quoteNumber}.pdf`);
  };

  // Gerar contrato PDF completo (após pagamento)
  const generateContractPDF = async () => {
    if (!quote) return;
    const { default: jsPDF } = await import('jspdf');
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = 210;
    const margin = 20;
    const contentWidth = pageWidth - margin * 2;
    let y = 20;

    const addTableRow = (label: string, value: string, highlight = false) => {
      if (highlight) {
        doc.setFillColor(240, 240, 240);
        doc.rect(margin, y - 4, contentWidth, 8, 'F');
      }
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(60, 60, 60);
      doc.text(label, margin + 2, y);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 30, 30);
      doc.text(value, pageWidth - margin - 2, y, { align: 'right' });
      y += 8;
    };

    // Header
    doc.setFillColor(30, 58, 138);
    doc.rect(0, 0, pageWidth, 40, 'F');
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('DRIVE WITH US', pageWidth / 2, 18, { align: 'center' });
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(200, 200, 255);
    doc.text('CONTRATO DE LOCAÇÃO DE VEÍCULO', pageWidth / 2, 28, { align: 'center' });
    const contractNumber = `DWUS-${Date.now().toString(36).toUpperCase()}`;
    doc.setFontSize(8);
    doc.setTextColor(180, 180, 220);
    doc.text(`Contrato Nº: ${contractNumber}`, pageWidth / 2, 36, { align: 'center' });

    y = 50;
    const today = new Date();
    const dateStr = today.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text(`Orlando, FL - ${dateStr}`, pageWidth - margin, y, { align: 'right' });
    y += 8;

    // Dados do Cliente
    doc.setFillColor(30, 58, 138);
    doc.rect(margin, y - 4, contentWidth, 8, 'F');
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('DADOS DO LOCATÁRIO', margin + 4, y + 1);
    y += 12;

    addTableRow('Nome Completo', clientName);
    addTableRow('Documento (CPF/Passaporte)', clientDocument, true);
    addTableRow('Telefone / WhatsApp', clientPhone);
    if (clientEmail) addTableRow('E-mail', clientEmail, true);
    if (clientAddress) addTableRow('Endereço', clientAddress);

    addTableRow('Forma de Pagamento', 'A combinar via WhatsApp');
    y += 4;

    // Veículo
    doc.setFillColor(30, 58, 138);
    doc.rect(margin, y - 4, contentWidth, 8, 'F');
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('DADOS DO VEÍCULO E PERÍODO', margin + 4, y + 1);
    y += 12;

    addTableRow('Veículo', quote.vehicleName);
    addTableRow('Período de Locação', `${formatDateBR(pickupDate)} a ${formatDateBR(returnDate)}`, true);
    addTableRow('Total de Diárias', `${quote.days} ${quote.days === 1 ? 'diária' : 'diárias'}`);
    addTableRow('Valor da Diária', `$${quote.pricePerDay.toFixed(2)}`, true);
    if (quote.childSeats > 0) {
      addTableRow('Cadeirinha de Criança', `${quote.childSeats} unidade(s) - Grátis (VIP)`);
    }
    y += 4;

    // Resumo Financeiro
    doc.setFillColor(197, 48, 48);
    doc.rect(margin, y - 4, contentWidth, 8, 'F');
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('RESUMO FINANCEIRO', margin + 4, y + 1);
    y += 12;

    addTableRow(`Aluguel do Veículo (${quote.days} diárias × $${quote.pricePerDay})`, `$${quote.subtotal.toFixed(2)}`);
    if (quote.discount > 0) {
      doc.setTextColor(22, 163, 74);
      addTableRow(`Desconto por Período (${(quote.discountRate * 100).toFixed(0)}%)`, `-$${quote.discount.toFixed(2)}`, true);
      doc.setTextColor(30, 30, 30);
    }
    addTableRow('Seguro Completo', 'Incluído');
    if (quote.gps > 0) addTableRow(`Toll Coverage Fee (${quote.days} diárias × $${FEES.gpsRentalFee})`, `$${quote.gps.toFixed(2)}`, true);
    addTableRow('Taxa de Aeroporto', `$${quote.airportFee.toFixed(2)}`);
    addTableRow(`Surcharge - state fee (${quote.days} diárias × $${FEES.cleaningFee})`, `$${quote.cleaningFee.toFixed(2)}`, true);
    addTableRow(`Impostos (${(FEES.taxRate * 100).toFixed(1)}%)`, `$${quote.tax.toFixed(2)}`);

    y += 2;
    doc.setFillColor(30, 58, 138);
    doc.rect(margin, y - 5, contentWidth, 12, 'F');
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('VALOR TOTAL', margin + 4, y + 2);
    doc.text(`$${quote.total.toFixed(2)}`, pageWidth - margin - 4, y + 2, { align: 'right' });
    y += 16;

    // Status de pagamento
    doc.setFillColor(220, 252, 231);
    doc.rect(margin, y - 4, contentWidth, 10, 'F');
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(22, 101, 52);
    doc.text('✓  PAGAMENTO CONFIRMADO - RESERVA GARANTIDA', pageWidth / 2, y + 2, { align: 'center' });
    y += 16;

    // Termos
    if (y > 220) {
      doc.addPage();
      y = 20;
    }

    doc.setFillColor(30, 58, 138);
    doc.rect(margin, y - 4, contentWidth, 8, 'F');
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('TERMOS E CONDIÇÕES', margin + 4, y + 1);
    y += 14;

    const terms = [
      '1. O veículo será entregue com o tanque cheio e deverá ser devolvido nas mesmas condições. Caso haja diferença, uma taxa poderá ser cobrada para cobrir o reabastecimento.',
      '2. O condutor deve ter mais de 21 anos e possuir CNH válida ou carteira de motorista internacional.',
      '3. O veículo não poderá ser utilizado fora do estado da Flórida sem autorização prévia por escrito.',
      '4. Qualquer multa de trânsito durante o período de locação será de responsabilidade do locatário.',
      '5. Em caso de acidente, o locatário deve comunicar imediatamente a Drive With US e as autoridades locais.',
      '6. O cancelamento com mais de 5 dias de antecedência terá reembolso integral. Outros cancelamentos poderão ter retenção de até 20% do valor.',
      '7. A DriveWith US reserva-se o direito de substituir o veículo por um de categoria igual ou superior, caso necessário.',
      '8. O seguro completo cobre danos ao veículo, terceiros e assistência 24h. Não cobre danos intencionais ou sob efeito de álcool/drogas.',
    ];

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(70, 70, 70);
    terms.forEach(term => {
      const lines = doc.splitTextToSize(term, contentWidth - 4);
      if (y + lines.length * 4 > 280) {
        doc.addPage();
        y = 20;
      }
      doc.text(lines, margin + 2, y);
      y += lines.length * 4 + 2;
    });

    y += 6;
    if (y > 240) {
      doc.addPage();
      y = 20;
    }

    y += 10;
    doc.setDrawColor(150, 150, 150);
    doc.setLineWidth(0.3);
    doc.line(margin, y, margin + 75, y);
    doc.line(pageWidth - margin - 75, y, pageWidth - margin, y);
    y += 5;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text('Assinatura do Locatário', margin, y);
    doc.text('DriveWith US - Locadora', pageWidth - margin - 75, y);
    y += 4;
    doc.setFontSize(8);
    doc.text(clientName, margin, y);
    doc.text('Orlando, FL - USA', pageWidth - margin - 75, y);

    const footerY = 285;
    doc.setDrawColor(30, 58, 138);
    doc.setLineWidth(0.5);
    doc.line(margin, footerY - 5, pageWidth - margin, footerY - 5);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(130, 130, 130);
    doc.text('DriveWith US • Orlando, FL • +1 (321) 594-9330 • dwusrental@gmail.com • @drivewitusoficial', pageWidth / 2, footerY, { align: 'center' });
    doc.text(`Documento gerado em ${today.toLocaleDateString('pt-BR')} às ${today.toLocaleTimeString('pt-BR')}`, pageWidth / 2, footerY + 4, { align: 'center' });

    doc.save(`Contrato_DWUS_${clientName.replace(/\s+/g, '_')}.pdf`);
  };

  // Submeter reserva via WhatsApp
  const handleBookingSubmit = async () => {
    if (!quote || !clientName.trim() || !clientEmail.trim() || !clientPhone.trim() || !clientDocument.trim()) return;

    setBookingSubmitting(true);

    // Montar mensagem WhatsApp com orçamento completo
    const whatsappMessage = 
      `🚗 SOLICITAÇÃO DE RESERVA - Drive With US\n\n` +
      `Olá! Gostaria de fechar a reserva abaixo:\n\n` +
      `--- DADOS DO CLIENTE ---\n` +
      `Nome: ${clientName}\n` +
      `Documento: ${clientDocument}\n` +
      `Telefone: ${clientPhone}\n` +
      `E-mail: ${clientEmail}\n` +
      (clientAddress ? `Endereço: ${clientAddress}\n` : '') +
      `\n--- ORÇAMENTO ---\n` +
      `Veículo: ${quote.vehicleName}\n` +
      `Período: ${formatDateBR(pickupDate)} a ${formatDateBR(returnDate)}\n` +
      `Diárias: ${quote.days}\n` +
      `Valor da Diária: $${quote.pricePerDay.toFixed(2)}\n` +
      (quote.discountRate > 0 ? `Desconto: ${(quote.discountRate * 100).toFixed(0)}%\n` : '') +
      (quote.childSeats > 0 ? `Cadeirinhas: ${quote.childSeats} unidade(s)\n` : '') +
      `Valor Total: $${quote.total.toFixed(2)}\n\n` +
      `Aguardo instruções para efetuar o pagamento e confirmar a reserva. Obrigado!`;
    
    // Abrir WhatsApp IMEDIATAMENTE (prioridade máxima)
    const encodedMessage = encodeURIComponent(whatsappMessage);
    const whatsappUrl = `https://wa.me/13215949330?text=${encodedMessage}`;
    
    // Abrir em nova aba
    window.open(whatsappUrl, '_blank');

    // Enviar dados do formulário em segundo plano (backup por email)
    try {
      const formBody = new URLSearchParams();
      formBody.append('name', clientName);
      formBody.append('email', clientEmail);
      formBody.append('phone', clientPhone);
      formBody.append('document', clientDocument);
      formBody.append('address', clientAddress);
      formBody.append('vehicle', quote.vehicleName);
      formBody.append('pickup_date', formatDateBR(pickupDate));
      formBody.append('return_date', formatDateBR(returnDate));
      formBody.append('days', String(quote.days));
      formBody.append('total', `$${quote.total.toFixed(2)}`);
      if (quote.childSeats > 0) formBody.append('child_seats', String(quote.childSeats));
      if (quote.discountRate > 0) formBody.append('discount', `${(quote.discountRate * 100).toFixed(0)}%`);

      fetch('https://readdy.ai/api/form/d6chlt3v53dtlm7j856g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      }).catch(() => {
        // Silencioso - não importa se falhar, o WhatsApp já foi aberto
      });
    } catch {
      // Silencioso
    }

    // Atualizar interface
    setShowBookingModal(false);
    setFlowStep('success');
    setShowSuccessPopup(true);
    setBookingSubmitting(false);
  };

  const openBookingModal = () => {
    setClientName('');
    setClientEmail('');
    setClientPhone('');
    setClientDocument('');
    setClientAddress('');
    setShowBookingModal(true);
  };

  const isStep1Valid = clientName.trim() !== '' && clientEmail.trim() !== '' && clientPhone.trim() !== '' && clientDocument.trim() !== '';

  return (
    <section id="orcamento" className="py-20 bg-gradient-to-br from-blue-100 to-red-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12">
          <h2 className="text-4xl md:text-5xl font-bold text-[#1e3a8a] text-center mb-4">
            Calcule Seu Orçamento em Segundos
          </h2>
          <p className="text-center text-gray-600 mb-12 text-lg">
            Preço final transparente com todos os detalhes incluídos.
          </p>

          {/* Formulário de Orçamento */}
          <form onSubmit={calculateQuote} className="space-y-6 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <i className="ri-calendar-line mr-2"></i>Data de Retirada
                </label>
                <input type="date" value={pickupDate} onChange={e => setPickupDate(e.target.value)} className="w-full px-4 py-3 border-2 border-[#1e3a8a]/20 rounded-lg focus:outline-none focus:border-[#1e3a8a] transition-colors" required />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <i className="ri-calendar-line mr-2"></i>Data de Devolução
                </label>
                <input type="date" value={returnDate} onChange={e => setReturnDate(e.target.value)} className="w-full px-4 py-3 border-2 border-[#1e3a8a]/20 rounded-lg focus:outline-none focus:border-[#1e3a8a] transition-colors" required />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <i className="ri-car-line mr-2"></i>Selecione o Veículo
                </label>
                <select value={selectedVehicle} onChange={e => setSelectedVehicle(e.target.value)} className="w-full px-4 py-3 border-2 border-[#1e3a8a]/20 rounded-lg focus:outline-none focus:border-[#1e3a8a] transition-colors cursor-pointer" required>
                  <option value="">Escolha um veículo</option>
                  {vehicles.map(vehicle => (
                    <option key={vehicle.id} value={vehicle.id}>{vehicle.name} {vehicle.year} - ${vehicle.pricePerDay}/dia</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg flex-1">
                <input type="checkbox" id="includeGPS" checked={includeGPS} onChange={e => setIncludeGPS(e.target.checked)} className="w-5 h-5 text-[#1e3a8a] border-gray-300 rounded focus:ring-[#1e3a8a] cursor-pointer" />
                <label htmlFor="includeGPS" className="text-sm font-medium text-gray-700 cursor-pointer">
                  <i className="ri-map-pin-line mr-2"></i>Incluir Toll Coverage Fee (${FEES.gpsRentalFee}/dia)
                </label>
              </div>
              <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg border border-amber-200 flex-1">
                <div className="flex items-center gap-3 flex-1">
                  <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-full flex-shrink-0">
                    <i className="ri-user-heart-line text-amber-700 text-lg"></i>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-gray-800">Cadeirinha de Criança</span>
                      <span className="text-[10px] font-bold bg-amber-500 text-white px-2 py-0.5 rounded-full whitespace-nowrap">VIP GRÁTIS</span>
                    </div>
                    <span className="text-xs text-gray-500">01 unidade por veículo GRATIS.</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => setChildSeats(Math.max(0, childSeats - 1))} className="w-8 h-8 flex items-center justify-center rounded-full border-2 border-amber-300 text-amber-700 hover:bg-amber-100 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed" disabled={childSeats === 0}>
                      <i className="ri-subtract-line text-sm"></i>
                    </button>
                    <span className="w-6 text-center font-bold text-gray-800 text-lg">{childSeats}</span>
                    <button type="button" onClick={() => setChildSeats(Math.min(3, childSeats + 1))} className="w-8 h-8 flex items-center justify-center rounded-full border-2 border-amber-300 text-amber-700 hover:bg-amber-100 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed" disabled={childSeats === 3}>
                      <i className="ri-add-line text-sm"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <button type="submit" className="w-full h-[56px] bg-[#c53030] text-white font-bold rounded-lg hover:bg-[#a02525] transition-all transform hover:scale-[1.02] flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer">
              <i className="ri-calculator-line text-xl"></i>
              Calcular Orçamento Completo
            </button>
          </form>

          {/* Resultado do Orçamento */}
          {quote && (
            <div ref={contractRef} className="bg-gradient-to-r from-red-100 to-blue-100 rounded-xl p-8 border-2 border-[#c53030]/20">
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-[#1e3a8a] mb-2">{quote.vehicleName}</h3>
                <p className="text-gray-600">Período: {quote.days} {quote.days === 1 ? 'diária' : 'diárias'}</p>
                {quote.discountRate > 0 && (
                  <div className="mt-2 inline-flex items-center gap-2 bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">
                    <i className="ri-price-tag-3-line"></i>Desconto de {(quote.discountRate * 100).toFixed(0)}% aplicado!
                  </div>
                )}
              </div>

              {/* Detalhamento */}
              <div className="space-y-3 mb-6">
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-gray-700">Aluguel do Veículo ({quote.days} {quote.days === 1 ? 'diária' : 'diárias'} × ${quote.pricePerDay})</span>
                  <span className="font-semibold text-gray-900">${quote.subtotal.toFixed(2)}</span>
                </div>
                {quote.discount > 0 && (
                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                    <span className="text-green-700 font-medium"><i className="ri-discount-percent-line mr-1"></i>Desconto por Período ({(quote.discountRate * 100).toFixed(0)}%)</span>
                    <span className="font-semibold text-green-700">-${quote.discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-gray-700">Seguro Completo (Incluso)</span>
                  <span className="font-semibold text-gray-900">Incluído</span>
                </div>
                {quote.gps > 0 && (
                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                    <span className="text-gray-700">Toll Coverage Fee ({quote.days} {quote.days === 1 ? 'diária' : 'diárias'} × ${FEES.gpsRentalFee})</span>
                    <span className="font-semibold text-gray-900">${quote.gps.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-gray-700">Taxa de Aeroporto</span>
                  <span className="font-semibold text-gray-900">${quote.airportFee.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-gray-700">Surcharge (state fee) ({quote.days} {quote.days === 1 ? 'diária' : 'diárias'} × ${FEES.cleaningFee})</span>
                  <span className="font-semibold text-gray-900">${quote.cleaningFee.toFixed(2)}</span>
                </div>
                {quote.childSeats > 0 && (
                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                    <span className="text-gray-700 flex items-center gap-2">
                      <span className="text-[10px] font-bold bg-amber-500 text-white px-1.5 py-0.5 rounded-full whitespace-nowrap">VIP</span>
                      Cadeirinha de Criança ({quote.childSeats} {quote.childSeats === 1 ? 'unidade' : 'unidades'})
                    </span>
                    <span className="font-semibold text-green-700">Grátis</span>
                  </div>
                )}
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-gray-700">Impostos ({(FEES.taxRate * 100).toFixed(1)}%)</span>
                  <span className="font-semibold text-gray-900">${quote.tax.toFixed(2)}</span>
                </div>
              </div>

              {/* Total */}
              <div className="border-t-2 border-[#c53030]/30 pt-6 mb-6">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-2xl font-bold text-gray-900">Valor Total</span>
                  <span className="text-4xl font-bold text-[#c53030]">${quote.total.toFixed(2)}</span>
                </div>
                <p className="text-sm text-gray-600 text-center">
                  <i className="ri-checkbox-circle-line text-green-600 mr-1"></i>
                  Todos os custos incluídos • Sem taxas ocultas • Suporte 24/7
                </p>
              </div>

              {/* Observações */}
              <div className="mb-8 p-5 bg-amber-50 border-l-4 border-amber-500 rounded-r-lg">
                <h4 className="font-bold text-amber-900 mb-3 flex items-center gap-2">
                  <i className="ri-alert-line text-lg"></i>Observações Importantes
                </h4>
                <ul className="space-y-2 text-sm text-amber-900">
                  <li className="flex items-start gap-2">
                    <i className="ri-gas-station-line text-amber-600 mt-0.5 flex-shrink-0"></i>
                    <span>O veículo será entregue com o tanque cheio e deverá ser devolvido nas mesmas condições.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-user-line text-amber-600 mt-0.5 flex-shrink-0"></i>
                    <span>Condutor deve ter mais de 21 anos e possuir CNH válida.</span>
                  </li>
                  {quote.childSeats > 0 && (
                    <li className="flex items-start gap-2">
                      <i className="ri-user-heart-line text-amber-600 mt-0.5 flex-shrink-0"></i>
                      <span>As cadeirinhas serão instaladas antes da entrega. Informe idade e peso da criança.</span>
                    </li>
                  )}
                </ul>
              </div>

              {/* Botões de Ação - Fluxo Principal */}
              {flowStep === 'quote' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Botão 1: Baixar PDF do Orçamento */}
                    <button
                      type="button"
                      onClick={generateQuotePDF}
                      className="group relative px-6 py-5 bg-white border-2 border-[#1e3a8a]/30 text-[#1e3a8a] font-bold rounded-xl hover:border-[#1e3a8a] hover:bg-[#1e3a8a]/5 transition-all whitespace-nowrap cursor-pointer flex items-center justify-center gap-3"
                    >
                      <div className="w-10 h-10 flex items-center justify-center bg-[#1e3a8a]/10 rounded-lg group-hover:bg-[#1e3a8a]/20 transition-colors">
                        <i className="ri-file-download-line text-xl"></i>
                      </div>
                      <div className="text-left">
                        <span className="block text-base">Baixar Orçamento PDF</span>
                        <span className="block text-xs font-normal text-gray-500">Salvar para analisar depois</span>
                      </div>
                    </button>

                    {/* Botão 2: Fechar Reserva via WhatsApp */}
                    <button
                      type="button"
                      onClick={openBookingModal}
                      className="group relative px-6 py-5 bg-gradient-to-r from-green-500 to-green-600 text-white font-bold rounded-xl hover:from-green-600 hover:to-green-700 transition-all whitespace-nowrap cursor-pointer flex items-center justify-center gap-3 shadow-lg shadow-green-500/20"
                    >
                      <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-lg group-hover:bg-white/30 transition-colors">
                        <i className="ri-whatsapp-line text-xl"></i>
                      </div>
                      <div className="text-left">
                        <span className="block text-base">Fechar Reserva</span>
                        <span className="block text-xs font-normal text-white/80">Enviar orçamento via WhatsApp</span>
                      </div>
                    </button>
                  </div>

                  <p className="text-center text-xs text-gray-500 mt-2">
                    <i className="ri-information-line mr-1"></i>
                    Preencha seus dados e envie o orçamento pelo WhatsApp para fechar sua reserva com nossa equipe.
                  </p>
                </div>
              )}

              {/* Estado de Sucesso */}
              {flowStep === 'success' && (
                <div className="space-y-4">
                  <div className="p-6 bg-green-50 border border-green-200 rounded-xl text-center">
                    <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                      <i className="ri-checkbox-circle-fill text-green-600 text-3xl"></i>
                    </div>
                    <h4 className="text-xl font-bold text-green-800 mb-2">Reserva Confirmada!</h4>
                    <p className="text-sm text-green-700 mb-1">Seu contrato foi gerado e baixado automaticamente.</p>
                    <p className="text-sm text-green-700">A confirmação foi enviada para <strong>{clientEmail}</strong>.</p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <button type="button" onClick={generateContractPDF} className="px-6 py-3 bg-[#1e3a8a] text-white font-semibold rounded-lg hover:bg-[#1e3a8a]/90 transition-all whitespace-nowrap cursor-pointer flex items-center justify-center gap-2">
                      <i className="ri-download-2-line text-lg"></i>Baixar Contrato Novamente
                    </button>
                    <button type="button" onClick={() => { setFlowStep('quote'); setQuote(null); }} className="px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-all whitespace-nowrap cursor-pointer flex items-center justify-center gap-2">
                      <i className="ri-restart-line text-lg"></i>Novo Orçamento
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ========== MODAL DE FECHAMENTO DE RESERVA ========== */}
          {showBookingModal && quote && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowBookingModal(false)}>
              <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                {/* Header do Modal */}
                <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 rounded-t-2xl">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-xl">
                        <i className="ri-whatsapp-line text-white text-2xl"></i>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">Fechar Reserva</h3>
                        <p className="text-white/70 text-sm">Preencha seus dados e envie pelo WhatsApp</p>
                      </div>
                    </div>
                    <button onClick={() => setShowBookingModal(false)} className="w-10 h-10 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors cursor-pointer">
                      <i className="ri-close-line text-xl"></i>
                    </button>
                  </div>
                </div>

                <div className="p-6">
                  {/* Resumo compacto */}
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200 mb-5">
                    <div className="flex items-center gap-2 mb-2">
                      <i className="ri-car-fill text-green-700"></i>
                      <span className="font-bold text-green-800 text-sm">{quote.vehicleName}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                      <div className="flex items-center gap-1.5"><i className="ri-calendar-check-line text-green-600"></i>{formatDateBR(pickupDate)}</div>
                      <div className="flex items-center gap-1.5"><i className="ri-calendar-close-line text-green-600"></i>{formatDateBR(returnDate)}</div>
                    </div>
                    <div className="flex justify-between items-center mt-2 pt-2 border-t border-green-200">
                      <span className="text-xs text-gray-600">{quote.days} {quote.days === 1 ? 'diária' : 'diárias'}</span>
                      <span className="text-lg font-bold text-green-700">${quote.total.toFixed(2)}</span>
                    </div>
                  </div>

                  {/* Formulário de Dados Pessoais */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Nome Completo <span className="text-[#c53030]">*</span></label>
                      <input type="text" value={clientName} onChange={e => setClientName(e.target.value)} placeholder="Seu nome completo" className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-green-500 transition-colors text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">CPF ou Passaporte <span className="text-[#c53030]">*</span></label>
                      <input type="text" value={clientDocument} onChange={e => setClientDocument(e.target.value)} placeholder="000.000.000-00 ou número do passaporte" className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-green-500 transition-colors text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">E-mail <span className="text-[#c53030]">*</span></label>
                      <div className="relative">
                        <input type="email" value={clientEmail} onChange={e => setClientEmail(e.target.value)} placeholder="seu@email.com" className="w-full px-4 py-3 pl-11 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-green-500 transition-colors text-sm" />
                        <i className="ri-mail-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Telefone / WhatsApp <span className="text-[#c53030]">*</span></label>
                      <div className="relative">
                        <input type="tel" value={clientPhone} onChange={e => setClientPhone(e.target.value)} placeholder="+55 (00) 00000-0000" className="w-full px-4 py-3 pl-11 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-green-500 transition-colors text-sm" />
                        <i className="ri-whatsapp-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Endereço</label>
                      <input type="text" value={clientAddress} onChange={e => setClientAddress(e.target.value)} placeholder="Cidade, Estado, País" className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-green-500 transition-colors text-sm" />
                    </div>

                    {/* Info do que vai acontecer */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-2.5">
                      <p className="text-xs font-bold text-gray-500 uppercase mb-2">Ao clicar, você será direcionado para:</p>
                      <div className="flex items-start gap-2.5">
                        <div className="w-6 h-6 flex items-center justify-center bg-green-100 rounded-full flex-shrink-0 mt-0.5">
                          <i className="ri-whatsapp-line text-green-600 text-xs"></i>
                        </div>
                        <p className="text-xs text-gray-700"><strong>WhatsApp</strong> com seu orçamento completo e dados preenchidos para nossa equipe finalizar a reserva</p>
                      </div>
                      <div className="flex items-start gap-2.5">
                        <div className="w-6 h-6 flex items-center justify-center bg-green-100 rounded-full flex-shrink-0 mt-0.5">
                          <i className="ri-money-dollar-circle-line text-green-600 text-xs"></i>
                        </div>
                        <p className="text-xs text-gray-700">Nossa equipe enviará as <strong>instruções de pagamento</strong> diretamente pelo WhatsApp</p>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={handleBookingSubmit}
                      disabled={!isStep1Valid || bookingSubmitting}
                      className="w-full py-4 bg-gradient-to-r from-green-500 to-green-600 text-white font-bold rounded-lg hover:from-green-600 hover:to-green-700 transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-green-500/20"
                    >
                      {bookingSubmitting ? (
                        <>
                          <i className="ri-loader-4-line animate-spin text-xl"></i>Enviando...
                        </>
                      ) : (
                        <>
                          <i className="ri-whatsapp-line text-xl"></i>Enviar Orçamento e Fechar Reserva
                        </>
                      )}
                    </button>

                    <p className="text-xs text-gray-500 text-center">
                      <i className="ri-lock-line mr-1"></i>Seus dados estão seguros e protegidos
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ========== POPUP DE SUCESSO ========== */}
          {showSuccessPopup && quote && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowSuccessPopup(false)}>
              <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden" onClick={e => e.stopPropagation()}>
                <div className="bg-gradient-to-r from-green-500 to-green-600 p-8 text-center relative">
                  <button onClick={() => setShowSuccessPopup(false)} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors cursor-pointer">
                    <i className="ri-close-line text-xl"></i>
                  </button>
                  <div className="w-20 h-20 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-4">
                    <i className="ri-whatsapp-line text-white text-4xl"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-1">Orçamento Enviado!</h3>
                  <p className="text-white/80 text-sm">Sua solicitação foi enviada pelo WhatsApp</p>
                </div>

                <div className="p-6 space-y-4">
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                      <div className="w-8 h-8 flex items-center justify-center bg-green-100 rounded-full flex-shrink-0">
                        <i className="ri-whatsapp-line text-green-600 text-sm"></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-green-800">WhatsApp enviado</p>
                        <p className="text-xs text-green-700">Nossa equipe já recebeu seu orçamento</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg">
                      <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-full flex-shrink-0">
                        <i className="ri-time-line text-amber-600 text-sm"></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-amber-800">Próximo passo</p>
                        <p className="text-xs text-amber-700">Aguarde as instruções de pagamento pelo WhatsApp</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                      <div className="w-8 h-8 flex items-center justify-center bg-green-100 rounded-full flex-shrink-0">
                        <i className="ri-shield-check-line text-green-600 text-sm"></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-green-800">Reserva segura</p>
                        <p className="text-xs text-green-700">Sua reserva só será confirmada após o pagamento</p>
                      </div>
                    </div>
                  </div>

                  {/* Resumo */}
                  <div className="border border-gray-200 rounded-lg p-4">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Resumo do Orçamento</p>
                    <div className="space-y-1.5 text-sm">
                      <div className="flex justify-between"><span className="text-gray-600">Cliente</span><span className="font-semibold text-gray-900">{clientName}</span></div>
                      <div className="flex justify-between"><span className="text-gray-600">Veículo</span><span className="font-semibold text-gray-900">{quote.vehicleName}</span></div>
                      <div className="flex justify-between"><span className="text-gray-600">Período</span><span className="font-semibold text-gray-900">{formatDateBR(pickupDate)} - {formatDateBR(returnDate)}</span></div>
                      <div className="flex justify-between pt-2 border-t border-gray-200">
                        <span className="font-bold text-gray-900">Valor Total</span>
                        <span className="font-bold text-green-700 text-lg">${quote.total.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  <button type="button" onClick={() => setShowSuccessPopup(false)} className="w-full py-3 bg-green-600 text-white font-bold rounded-lg hover:bg-green-700 transition-all whitespace-nowrap cursor-pointer flex items-center justify-center gap-2">
                    <i className="ri-check-line text-lg"></i>Entendido
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
